import React, { useState, useEffect } from 'react';
import { UserIcon, ClockIcon } from '@heroicons/react/24/outline';
import sessionService, { SessionsResponse } from '../services/sessionService';
import { useAuth } from '../contexts/AuthContext';

interface OnlineUsersProps {
  className?: string;
  showDetails?: boolean;
}

const OnlineUsers: React.FC<OnlineUsersProps> = ({ 
  className = '', 
  showDetails = true 
}) => {
  const [activeSessions, setActiveSessions] = useState<SessionsResponse>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user: currentUser } = useAuth();

  // Charger les sessions actives
  const loadActiveSessions = async () => {
    try {
      setError(null);
      const sessions = await sessionService.getActiveSessions();
      setActiveSessions(sessions);
    } catch (error: any) {
      console.error('Erreur lors du chargement des sessions actives:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadActiveSessions();
    
    // Rafraîchir les sessions toutes les 10 secondes (plus fréquent)
    const interval = setInterval(loadActiveSessions, 10000);
    
    // Rafraîchir quand la fenêtre reprend le focus
    const handleFocus = () => {
      loadActiveSessions();
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Rafraîchir les données toutes les secondes pour les durées affichées
  useEffect(() => {
    if (showDetails && Object.keys(activeSessions).length > 0) {
      const updateInterval = setInterval(() => {
        // Force un re-render pour mettre à jour les durées affichées
        setActiveSessions(prev => ({ ...prev }));
      }, 1000);
      
      return () => clearInterval(updateInterval);
    }
  }, [showDetails, activeSessions]);

  const sessionsList = Object.values(activeSessions);
  const onlineCount = sessionsList.length;

  if (isLoading) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="animate-pulse flex items-center space-x-2">
          <div className="h-4 w-4 bg-gray-300 rounded-full"></div>
          <div className="h-4 w-16 bg-gray-300 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`text-red-600 text-sm ${className}`}>
        Erreur: {error}
      </div>
    );
  }

  if (!showDetails) {
    return (
      <div className={`flex items-center space-x-2 ${className}`}>
        <div className="flex items-center space-x-1">
          <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
          <UserIcon className="h-4 w-4 text-green-600" />
          <span className="text-sm font-medium text-green-600">
            {onlineCount} connecté{onlineCount > 1 ? 's' : ''}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Utilisateurs connectés
          </h3>
          <div className="flex items-center space-x-1">
            <div className="h-2 w-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-green-600 dark:text-green-400">
              {onlineCount} en ligne
            </span>
          </div>
        </div>

        {onlineCount === 0 ? (
          <div className="text-center py-4 text-gray-500 dark:text-gray-400">
            Aucun utilisateur connecté
          </div>
        ) : (
          <div className="space-y-3">
            {sessionsList.map((session) => {
              const isCurrentUser = session.userId === currentUser?.id;
              
              return (
                <div
                  key={session.userId}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    isCurrentUser 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' 
                      : 'bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className="relative">
                      <UserIcon className={`h-8 w-8 ${
                        isCurrentUser 
                          ? 'text-blue-600 dark:text-blue-400' 
                          : 'text-gray-600 dark:text-gray-400'
                      }`} />
                      <div className="absolute -top-1 -right-1 h-3 w-3 bg-green-400 border-2 border-white dark:border-gray-800 rounded-full"></div>
                    </div>
                    
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className={`font-medium ${
                          isCurrentUser 
                            ? 'text-blue-900 dark:text-blue-100' 
                            : 'text-gray-900 dark:text-gray-100'
                        }`}>
                          {session.name}
                        </span>
                        {isCurrentUser && (
                          <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded font-medium">
                            Vous
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        @{session.username}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="flex items-center space-x-1 text-xs text-gray-500 dark:text-gray-400">
                      <ClockIcon className="h-3 w-3" />
                      <span>
                        {sessionService.formatLastActivity(session.lastActivity)}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Connecté depuis {sessionService.formatConnectionDuration(session.connectedAt)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OnlineUsers; 